import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import {
  candidatePage,
  consentPage,
  consentBody,
  submitConsent,
  submitWithdrawal,
  recoverCommand,
  cancelUnrecorded,
  fingerprint,
  restorePending,
  type PendingCommand,
  type ConsentCommand,
  type ConsentStatus,
} from '@/services/vault/historicalDocuments'
import { serviceLabels, categoryLabels } from '@/services/vault/sourceReviews'
import { CommandStatus, useCommand, useStepUp } from './VaultForms'

export default function HistoricalDocumentsPanel({
  client,
  owner,
  accountKey,
}: {
  client: string
  owner: boolean
  accountKey: unknown[]
}) {
  const { user } = useAuth()
  const allowed =
    owner &&
    user?.authScope === 'client_vault' &&
    !!user.currentSessionId &&
    ['individual_client', 'business_owner_client'].includes(user.role)
  const authority = JSON.stringify([
    accountKey,
    client,
    user?.id,
    user?.currentSessionId,
    user?.role,
    user?.permissions,
  ])
  if (!allowed) return <p role="alert">Current verified client ownership is required.</p>
  return (
    <HistoricalWorkspace
      key={authority}
      client={client}
      accountKey={accountKey}
      authority={authority}
      storageKey={'vault-historical-command:' + JSON.stringify([user!.id, client])}
    />
  )
}

function HistoricalWorkspace({
  client,
  accountKey,
  authority,
  storageKey,
}: {
  client: string
  accountKey: unknown[]
  authority: string
  storageKey: string
}) {
  const queryClient = useQueryClient(),
    feedback = useCommand(),
    stepUp = useStepUp()
  const serializedKey = JSON.stringify([...accountKey, client, 'historical-documents', authority])
  const key = useMemo<unknown[]>(() => JSON.parse(serializedKey), [serializedKey])
  const [page, setPage] = useState(1),
    [historyPage, setHistoryPage] = useState(1)
  const [selected, setSelected] = useState(''),
    [ack, setAck] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [resolution, setResolution] = useState('')
  const [pending, setPending] = useState<PendingCommand | null>(() =>
    restorePending(storageKey, client)
  )
  const original = useRef<ConsentCommand | null>(null),
    inFlight = useRef(false),
    mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      queryClient.removeQueries({ queryKey: key })
    }
  }, [queryClient, key])
  const choices = useQuery({
    queryKey: [...key, 'candidates', page],
    queryFn: () => candidatePage(client, page),
    retry: false,
    staleTime: 0,
  })
  const history = useQuery({
    queryKey: [...key, 'consents', historyPage],
    queryFn: () => consentPage(client, historyPage),
    retry: false,
    staleTime: 0,
  })
  const currentChoices = choices.isSuccess && !choices.isFetching ? choices.data : undefined
  const currentHistory = history.isSuccess && !history.isFetching ? history.data : undefined
  const candidate = currentChoices?.items.find(
    item => item.manifest_run_id + ':' + item.source_document_id === selected
  )
  const binding = candidate ? JSON.stringify([authority, candidate, choices.dataUpdatedAt]) : null
  const alreadyActive = currentHistory?.items.some(
    item =>
      candidate &&
      item.source_document_id === candidate.source_document_id &&
      item.manifest_run_id === candidate.manifest_run_id &&
      ['active', 'withdrawal_pending'].includes(item.state)
  )
  const canConsent = !!candidate && !!currentHistory && !pending && !alreadyActive && !feedback.busy
  useEffect(() => {
    setAck(null)
  }, [choices.isFetching, choices.isError, choices.dataUpdatedAt])
  const refresh = async () => {
    setAck(null)
    await queryClient.invalidateQueries({ queryKey: key })
  }
  const forget = () => {
    sessionStorage.removeItem(storageKey)
    setPending(null)
    original.current = null
  }
  const transmit = async (reference: PendingCommand, body: ConsentCommand | null) => {
    if (reference.operation === 'consent') {
      if (!body || (await fingerprint(body)) !== reference.fingerprint)
        throw new Error(
          'The original source details changed. Keep this request pending for review.'
        )
      await submitConsent(client, body)
    } else {
      if (
        (await fingerprint({ consent: reference.consent, idempotency_key: reference.key })) !==
        reference.fingerprint
      )
        throw new Error(
          'The original withdrawal reference changed. Keep this request pending for review.'
        )
      await submitWithdrawal(client, reference.consent!, reference.key)
    }
    if (!mounted.current) return
    forget()
    await refresh()
  }
  const consent = () => {
    if (!canConsent || !binding || ack !== binding || !candidate || inFlight.current) return
    inFlight.current = true
    const body = consentBody(candidate, crypto.randomUUID())
    setAck(null)
    void feedback.run(async () => {
      try {
        const reference: PendingCommand = {
          version: 1,
          client,
          key: body.idempotency_key,
          operation: 'consent',
          source: body.source_document_id,
          run: body.manifest_run_id,
          consent: null,
          fingerprint: await fingerprint(body),
        }
        // Persist only identifiers/digest before POST, never names, source facts or MFA codes.
        sessionStorage.setItem(storageKey, JSON.stringify(reference))
        if (!mounted.current) return
        setPending(reference)
        original.current = body
        await transmit(reference, body)
      } finally {
        inFlight.current = false
      }
    })
  }
  const withdraw = (status: ConsentStatus) => {
    if (
      feedback.busy ||
      pending ||
      inFlight.current ||
      !['active', 'expired'].includes(status.state)
    )
      return
    inFlight.current = true
    void feedback.run(async () => {
      try {
        const id = crypto.randomUUID()
        const reference: PendingCommand = {
          version: 1,
          client,
          key: id,
          operation: 'withdrawal',
          source: status.source_document_id,
          run: status.manifest_run_id,
          consent: status.consent_id,
          fingerprint: await fingerprint({ consent: status.consent_id, idempotency_key: id }),
        }
        sessionStorage.setItem(storageKey, JSON.stringify(reference))
        if (!mounted.current) return
        setPending(reference)
        await transmit(reference, null)
      } finally {
        inFlight.current = false
      }
    })
  }
  const retry = () => {
    if (!pending || feedback.busy || inFlight.current) return
    inFlight.current = true
    const reference = pending
    const source = currentChoices?.items.find(
      item => item.source_document_id === reference.source && item.manifest_run_id === reference.run
    )
    const body = original.current ?? (source ? consentBody(source, reference.key) : null)
    void feedback.run(async () => {
      try {
        await transmit(reference, body)
      } finally {
        inFlight.current = false
      }
    })
  }
  const recover = () => {
    if (!pending || feedback.busy || inFlight.current) return
    inFlight.current = true
    const reference = pending
    void feedback.run(async () => {
      try {
        const outcome = await recoverCommand(reference)
        if (!mounted.current) return
        setResolution(
          outcome === null
            ? 'Original unrecorded request cancelled. Its key cannot record consent.'
            : 'Original request recorded. Check its current status below.'
        )
        forget()
        await refresh()
      } finally {
        inFlight.current = false
      }
    })
  }
  const cancelPending = () => {
    if (!pending || pending.operation !== 'consent' || feedback.busy || inFlight.current) return
    inFlight.current = true
    const reference = pending
    void feedback.run(async () => {
      try {
        const outcome = await cancelUnrecorded(reference)
        if (!mounted.current) return
        setResolution(
          outcome === null
            ? 'Original unrecorded request cancelled. Its key cannot record consent.'
            : 'Consent was already recorded. Use withdrawal below if you want to stop its authorization.'
        )
        forget()
        await refresh()
      } finally {
        inFlight.current = false
      }
    })
  }
  const labels: Record<ConsentStatus['state'], string> = {
    active: 'Consent recorded',
    expired: 'Consent expired',
    withdrawn: 'Withdrawal effective',
    withdrawal_pending: 'Withdrawal pending copy reconciliation',
  }
  return (
    <div className="min-w-0 space-y-5 break-words text-text-primary [&_button]:max-w-full [&_button]:whitespace-normal">
      <section className="card card-elevated p-5 space-y-4" aria-label="Historical documents">
        <h2 className="font-semibold">Bring historical documents into your Vault</h2>
        <p className="text-sm text-text-secondary">
          Review an observed document before consenting to a separate client-owned copy. The firm's
          original is preserved. Consent does not start or complete copying; an authorized firm must
          request the exact copy, which must pass its checks and scanning.
        </p>
        <form
          className="space-y-2"
          onSubmit={event => {
            event.preventDefault()
            if (feedback.busy || inFlight.current) return
            void feedback.run(async () => {
              await stepUp(code)
              if (mounted.current) {
                setCode('')
                await refresh()
              }
            })
          }}
        >
          <label className="block text-sm">
            Authenticator code
            <input
              className="input input-bordered block"
              type="password"
              autoComplete="one-time-code"
              value={code}
              required
              onChange={event => setCode(event.target.value)}
            />
          </label>
          <button className="btn btn-secondary" disabled={feedback.busy}>
            Verify identity and refresh historical documents
          </button>
        </form>
        <button
          className="btn btn-secondary"
          disabled={feedback.busy || choices.isFetching || history.isFetching}
          onClick={() => void refresh()}
        >
          Refresh historical documents
        </button>
        {choices.isFetching && (
          <p role="status">Checking current ownership and observed documents…</p>
        )}
        {choices.error && <p role="alert">{choices.error.message}</p>}
        {currentChoices && (
          <>
            {!currentChoices.items.length && (
              <p>
                No eligible documents on this page.
                {currentChoices.has_more
                  ? ' More observed sources are available on later pages.'
                  : ''}
              </p>
            )}
            {!!currentChoices.items.length && (
              <label className="block text-sm">
                Observed document
                <select
                  className="input input-bordered block w-full"
                  value={selected}
                  disabled={feedback.busy || !!pending}
                  onChange={event => {
                    setSelected(event.target.value)
                    setAck(null)
                  }}
                >
                  <option value="">Choose a document to review</option>
                  {currentChoices.items.map(item => (
                    <option
                      key={item.source_document_id}
                      value={item.manifest_run_id + ':' + item.source_document_id}
                    >
                      {item.filename} ·{' '}
                      {categoryLabels[item.metadata.category] ?? item.metadata.category}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="flex gap-3">
              <button
                className="btn btn-secondary"
                disabled={page === 1 || feedback.busy || !!pending}
                onClick={() => {
                  setPage(page - 1)
                  setSelected('')
                  setAck(null)
                }}
              >
                Previous document page
              </button>
              <button
                className="btn btn-secondary"
                disabled={!currentChoices.has_more || feedback.busy || !!pending}
                onClick={() => {
                  setPage(page + 1)
                  setSelected('')
                  setAck(null)
                }}
              >
                Next document page
              </button>
            </div>
          </>
        )}
        {candidate && (
          <div className="space-y-3">
            <h3 className="font-medium">{candidate.filename}</h3>
            <p>
              {serviceLabels[candidate.metadata.service]} ·{' '}
              {categoryLabels[candidate.metadata.category] ?? candidate.metadata.category} ·{' '}
              {candidate.metadata.financial_year ?? 'Undated'} ·{' '}
              {candidate.byte_size.toLocaleString()} bytes
            </p>
            <p className="text-sm">
              The source will be checked again before copying. A changed or unavailable source
              cannot be copied using this consent.
            </p>
            {alreadyActive && (
              <p role="status">
                Consent is already recorded for this observed source. Review its current status
                below.
              </p>
            )}
            <label className="flex gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!binding && ack === binding}
                disabled={!canConsent}
                onChange={event => setAck(event.target.checked ? binding : null)}
              />
              I consent to the exact observed document being copied into my client-owned Vault,
              preserving the firm's original and its retained history.
            </label>
            <button
              className="btn btn-primary"
              disabled={!canConsent || !binding || ack !== binding}
              onClick={consent}
            >
              Record historical document consent
            </button>
          </div>
        )}
      </section>
      {resolution && <p role="status">{resolution}</p>}
      {pending && (
        <section className="card p-5 space-y-3" aria-label="Pending historical request">
          <h2 className="font-semibold">
            Original {pending.operation} request awaiting confirmation
          </h2>
          <p>
            Keep this request pending until its outcome is confirmed. A missing outcome does not
            mean that the original request failed.
          </p>
          <button className="btn btn-secondary" disabled={feedback.busy} onClick={recover}>
            Check original request outcome
          </button>
          <button
            className="btn btn-secondary"
            disabled={
              feedback.busy ||
              (pending.operation === 'consent' &&
                !original.current &&
                !currentChoices?.items.some(
                  item =>
                    item.source_document_id === pending.source &&
                    item.manifest_run_id === pending.run
                ))
            }
            onClick={retry}
          >
            Retry original {pending.operation} request
          </button>
          {pending.operation === 'consent' && (
            <div className="space-y-2">
              <p>
                Cancel only if the original consent was never recorded. If it already exists, this
                checks its status; stopping authorization still requires withdrawal.
              </p>
              <button
                className="btn btn-secondary"
                disabled={feedback.busy}
                onClick={cancelPending}
              >
                Cancel original request if unrecorded
              </button>
            </div>
          )}
        </section>
      )}
      <section className="card card-elevated p-5 space-y-4" aria-label="Historical consent status">
        <h2 className="font-semibold">Historical document consents</h2>
        <p className="text-sm text-text-secondary">
          Withdrawal stops unused authorization. If a copy was already reserved, withdrawal remains
          pending until that copy's outcome is reconciled. Existing published history and the firm's
          original records are retained.
        </p>
        {history.isFetching && <p role="status">Checking current consent and withdrawal status…</p>}
        {history.error && <p role="alert">{history.error.message}</p>}
        {currentHistory && (
          <>
            {!currentHistory.items.length && <p>No consents recorded on this page.</p>}
            {currentHistory.items.map(status => (
              <article key={status.consent_id} className="space-y-2 border rounded p-3">
                <h3 id={'historical-consent-' + status.consent_id}>
                  {categoryLabels[status.metadata.category] ?? status.metadata.category} ·{' '}
                  {serviceLabels[status.metadata.service]} ·{' '}
                  {status.metadata.financial_year ?? 'Undated'}
                </h3>
                <p role="status">{labels[status.state]}</p>
                {status.pending_operation_id && status.state !== 'withdrawal_pending' && (
                  <p>Copy outcome is awaiting reconciliation.</p>
                )}
                <p className="text-sm">
                  Consent expires: {new Date(status.expires_at).toLocaleString()}
                </p>
                {['active', 'expired'].includes(status.state) && (
                  <button
                    className="btn btn-secondary"
                    disabled={feedback.busy || !!pending}
                    onClick={() => withdraw(status)}
                    aria-describedby={'historical-consent-' + status.consent_id}
                  >
                    Withdraw this consent
                  </button>
                )}
              </article>
            ))}
            <div className="flex gap-3">
              <button
                className="btn btn-secondary"
                disabled={historyPage === 1 || feedback.busy}
                onClick={() => setHistoryPage(historyPage - 1)}
              >
                Previous consent page
              </button>
              <button
                className="btn btn-secondary"
                disabled={!currentHistory.has_more || feedback.busy}
                onClick={() => setHistoryPage(historyPage + 1)}
              >
                Next consent page
              </button>
            </div>
          </>
        )}
      </section>
      <CommandStatus {...feedback} />
    </div>
  )
}
